import Link from 'next/link';
import Image from 'next/image';

import Layout from '@/components/Layout/Vanilla';
import Button from '@/components/Button';
import { DefaultLogoReel } from '@/components/LogoReel';
import USPCardRedesigned from '@/components/USPCardRedesigned';
import Playground from '@/components/Playground';
import { fetchPage, gql } from '@/lib/graphcms';
import { queries, mutations } from '@/utils/demo-for-engineering';

const enablingYouToBuildYourBest = [
  {
    image: {
      url: '/images/for-engineering/localized-content.svg',
      width: 259,
      height: 195,
    },
    title: 'Localized Content',
    description:
      'Whether you’re handling corporate presence in multiple regions or delivering locale-specific products, our out-of-the-box localization sets you up for success.',
    ctaTo: '/use-cases/content-localization',
  },
  {
    image: {
      url: '/images/for-engineering/structured-content.svg',
      width: 336,
      height: 231,
    },
    title: 'Structured Content',
    description:
      'Easily create, iterate on, and enrich your content. Further enhance it through third-party APIs, and unlock programmatic approaches to managing and delivering content.',
    ctaTo: '/use-cases/structured-content',
  },
  {
    image: {
      url: '/images/for-engineering/content-federation.svg',
      width: 255,
      height: 203,
    },
    title: 'Content Federation',
    description:
      'Break down your content silos. GraphCMS provides you with central control of your enterprise content from multiple sources through a single, unified content layer.',
    ctaTo: '/use-cases/content-federation',
  },
];

const structureYourContent = [
  {
    icon: 'LIST_CHECK_2',
    title: 'Large set of field types',
    description:
      'All the building blocks you need. Localize or add custom constraints and input validations wherever needed.',
  },
  {
    icon: 'StackLineIcon',
    title: 'Embedded types and components',
    description:
      'Define reusable components for dynamic field sections. Empower content creators to build their own content layouts within their given boundries.',
  },
  {
    icon: 'TerminalLineIcon',
    title: 'Access via UI, API or SDK',
    description:
      'Build and evolve your content model the way it fits best into your workflow. Use the intuitive UI or execute schema changes from code and integrate in your CI/CD processes.',
  },
];

const manageYourContent = [
  {
    icon: 'Layout3LineIcon',
    title: 'Fast and lightweight content studio',
    description:
      'Iterate quickly with an intuitive content management interface for your demanding content creation processes.',
  },
  {
    icon: 'EyeLineIcon',
    title: 'Content preview and staging',
    description:
      'Our customizable content staging allows you to set up safe drafting, preview and publishing processes.',
  },
  {
    icon: 'Attachment2Icon',
    title: 'Digital asset management',
    description:
      'Our inbuilt digital asset management helps you to organize your assets. Add custom metadata and serve assets via a transformation pipeline from a global and fast CDN.',
  },
];

const buildFastApplicationsFaster = [
  {
    icon: 'DeviceLineIcon',
    title: 'Frontend freedom',
    description:
      'With a headless CMS, content is well structured and separated from its presentation. Develop applications for the platform and framework of your choice.',
  },
  {
    icon: 'GRAPHQL',
    title: 'Add value to your product',
    description:
      'Our team of experts is constantly improving and monitoring our enterprise-grade GraphQL engine and infrastructure, so you don’t have to. Focus on adding value to your product.',
  },
  {
    icon: 'TreasureMapLineIcon',
    title: 'Adapt to future trends',
    description:
      'The web and frontend technology ecosystem is evolving rapidly. Adapt to future trends and stacks without being bound to outdated templating languages and frameworks.',
  },
];

const weGenerateYourAPI = [
  {
    icon: 'CODE_FILL',
    title: 'Query and mutation API',
    description:
      'GraphCMS is GraphQL native. Fetch your content with a single API call or ingest data using the mutation API.',
  },
  {
    icon: 'Battery2ChargeLineIcon',
    title: 'Batteries included',
    description:
      'Countless helpers for filtering, sorting, pagination, localization, content staging, publishing or asset transformations included.',
  },
  {
    icon: 'ShieldCheckLineIcon',
    title: 'Powerful permissions',
    description:
      'Granular role based access control for both, your CMS users and API consumers. Express model, field and conditional rules.',
  },
];

const iterateQuickly = [
  {
    icon: 'GitBranchLineIcon',
    title: 'Multiple environments',
    description:
      'Safely iterate on isolated instances of your project without breaking your production systems.',
  },
  {
    icon: 'Medal2FillIcon',
    title: 'Promote environments',
    description:
      'Once your development of a new feature is completed, promote development environments as your new production environments instantly.',
  },
  {
    icon: 'RefreshLineIcon',
    title: 'Integrate with your CI/CD',
    description:
      'Execute all changes programmatically, using the management SDK. Integrate seamlessly into your deployment process.',
  },
];

export default function ForEngineering({ page }) {
  const { usps } = page;

  return (
    <Layout seo={page}>
      <section className="py-6 md:py-12">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-6 md:pt-12">
            <div className="w-full md:w-2/5 space-y-6">
              <p className="text-cornflower font-semibold uppercase tracking-wider">
                For Engineering
              </p>
              <h1 className="font-bold leading-tight text-3xl md:text-5xl">
                {page.title}
              </h1>
              <p className="leading-relaxed md:text-lg text-zodiac">
                {page.subTitle}
              </p>
              <Button href="https://app.graphcms.com/signup" colour="purple">
                Sign up for free &#8594;
              </Button>
            </div>

            <div className="w-full md:w-3/5">
              <div className="relative flex items-center justify-center">
                <Image
                  src="/images/for-engineering/graphcms-build-on-your-terms.svg"
                  alt=""
                  width={514}
                  height={348}
                />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 pt-12 md:pb-6 lg:pt-20">
            {usps.map((usp, index) => (
              <USPCardRedesigned key={index} {...usp} theme="light" />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-whisper py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <DefaultLogoReel />
        </div>
      </section>

      <section className="bg-haiti py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="mx-auto space-y-6 text-center w-full pb-6 md:pb-12">
            <h2 className="text-white font-bold leading-tight text-3xl md:text-5xl">
              Build Your Ultimate Content Pipeline
            </h2>
            <p className="text-whisper">
              Content orchestration pipeline for market-leading digital products
              and services.
            </p>
          </div>

          <div className="hidden md:block max-w-4xl mx-auto">
            <Image
              src="/images/for-engineering/full-content-pipeline.svg"
              className="block"
              alt=""
              width={940}
              height={522}
            />
          </div>

          <div className="md:hidden">
            <div className="py-6 space-y-3">
              <Image
                src="/images/for-engineering/content-sources.svg"
                className="block mb-12"
                alt=""
                width={592}
                height={522}
              />
              <p className="text-cornflower font-medium uppercase tracking-widest text-sm">
                Sources
              </p>
              <h2 className="text-white font-bold leading-tight text-2xl">
                Create Your Content
              </h2>
              <p className="text-whisper">
                Within minutes, define everything about your content API.
                Structures, localizations, relations, powerful permissions and
                more…
              </p>
            </div>

            <div className="py-6 space-y-3">
              <Image
                src="/images/for-engineering/content-destinations.svg"
                className="block mb-12"
                alt=""
                width={598}
                height={522}
              />
              <p className="text-cornflower font-medium uppercase tracking-widest text-sm">
                Destinations
              </p>
              <h2 className="text-white font-bold leading-tight text-2xl">
                Use Your Content
              </h2>
              <p className="text-whisper">
                Within minutes, define everything about your content API.
                Structures, localizations, relations, powerful permissions and
                more…
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="mx-auto space-y-6 text-center w-full max-w-3xl pb-6 md:pb-20">
            <h2 className="text-haiti font-bold leading-tight text-3xl md:text-5xl">
              Enabling You to Build Your Best
            </h2>
            <p className="text-zodiac">
              GraphCMS gives you the ability to manage content with the
              flexibility of a database, and the usability of a CMS - unlocking
              solutions and use-cases that can’t be handled by a generic
              Headless CMS.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {enablingYouToBuildYourBest.map(
              ({ image, title, description, cta, ctaTo }) => (
                <div
                  key={ctaTo}
                  className="space-y-3 md:space-y-6 lg:space-y-12"
                >
                  <div className="h-56 flex items-center justify-center">
                    <Image
                      src={image.url}
                      alt=""
                      width={image.width}
                      height={image.height}
                    />
                  </div>
                  <div className="space-y-3 md:space-y-4 py-3">
                    <h3 className="text-haiti font-bold text-xl">{title}</h3>
                    <p className="text-light-haiti">{description}</p>
                    {ctaTo && (
                      <div>
                        <Link href={ctaTo} prefetch={false}>
                          <a className="font-medium group text-cornflower">
                            {cta || 'Learn more'}
                            <span className="duration-100 group-hover:translate-x-1 inline-block ml-1 transform transition-transform">
                              &#8594;
                            </span>
                          </a>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              ),
            )}
          </div>

          <div className="pt-10 md:pt-12 flex justify-center">
            <div className="w-full md:w-1/3 md:px-8">
              <Button
                href="/use-cases"
                appearance="outline"
                colour="purple"
                block
              >
                See all solutions &#8594;
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-whisper py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="mx-auto space-y-6 text-center w-full max-w-2xl pb-12">
            <h2 className="text-haiti font-bold leading-tight text-3xl md:text-5xl">
              How GraphCMS Works
            </h2>
            <p className="text-zodiac">
              Empower your development and editorial teams&apos; workflows and
              bring your digital projects to life with GraphCMS. Powerful
              GraphQL API for developers, and a seamless editor experience for
              content creators.
            </p>
          </div>

          <div className="w-full md:w-1/2">
            <div className="relative inline-flex items-center pb-4">
              <span className="text-cornflower font-semibold text-sm pr-3">
                01
              </span>
              <span className="h-0.5 w-24 bg-cornflower" />
            </div>
            <h3 className="font-bold text-xl md:text-2xl lg:text-3xl leading-snug mb-4 text-haiti">
              Structure Your Content
            </h3>
            <p className="text-zodiac">
              Within minutes, define everything about your content API.
              Structures, localizations, relations, powerful permissions and
              more…
            </p>
          </div>

          <div className="flex flex-col-reverse md:flex-row items-center space-y-6 md:space-y-0 md:space-x-6 md:space-x-12">
            <div className="w-full md:w-1/2">
              <div className="space-y-6 pt-6">
                {structureYourContent.map((usp, index) => (
                  <USPCardRedesigned
                    key={index}
                    {...usp}
                    theme="light"
                    direction="row"
                  />
                ))}
              </div>
            </div>
            <div className="w-full md:w-1/2 h-full md:flex md:items-center md:justify-center">
              <Image
                src="/images/for-engineering/structure-your-content.svg"
                className="md:-mt-20"
                alt=""
                width={575}
                height={382}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-haiti py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="mx-auto space-y-6 md:text-center w-full md:w-3/4">
            <div className="relative inline-flex items-center">
              <span className="text-cornflower font-semibold text-sm pr-3">
                02
              </span>
              <span className="h-0.5 w-24 bg-cornflower" />
            </div>
            <h2 className="text-white font-bold text-xl md:text-2xl lg:text-3xl leading-snug">
              We Generate Your API
            </h2>
            <p className="text-whisper">
              Use your production-ready GraphQL API to deliver content to any
              platform of your choice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 pt-12 md:pb-6 lg:pt-20 text-whisper">
            {weGenerateYourAPI.map((usp, index) => (
              <USPCardRedesigned key={index} {...usp} theme="purple" />
            ))}
          </div>

          <div className="pt-6 md:pt-12">
            <Playground queries={queries} mutations={mutations} />
          </div>
        </div>
      </section>

      <section className="bg-whisper py-20 md:py-24 space-y-12 md:space-y-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="w-full md:w-1/2">
            <div className="relative inline-flex items-center pb-4">
              <span className="text-cornflower font-semibold text-sm pr-3">
                03
              </span>
              <span className="h-0.5 w-24 bg-cornflower" />
            </div>
            <h3 className="font-bold text-xl md:text-2xl lg:text-3xl leading-snug mb-4 text-haiti">
              Manage Your Content
            </h3>
            <p className="text-zodiac">
              We give your content creators the tools they&apos;re used to -
              text editors, workflows, advanced roles and permissions. All
              content can also be managed programmatically using the mutation
              API.
            </p>
          </div>

          <div className="flex flex-col-reverse md:flex-row items-center space-y-6 md:space-y-0 md:space-x-6 md:space-x-12 relative">
            <div className="w-full md:w-1/2">
              <div className="space-y-6 py-6">
                {manageYourContent.map((usp, index) => (
                  <USPCardRedesigned
                    key={index}
                    {...usp}
                    theme="light"
                    direction="row"
                  />
                ))}
              </div>
              <Link href="/for-content-creators" prefetch={false}>
                <a className="font-medium group text-cornflower">
                  Learn more about managing content
                  <span className="duration-100 group-hover:translate-x-1 inline-block ml-1 transform transition-transform">
                    &#8594;
                  </span>
                </a>
              </Link>
            </div>
            <div className="w-full md:w-1/2">
              {/* <div className="md:absolute md:top-0 md:-inset-x-1/2 md:-mt-48 md:-mr-72 md:-ml-12 inline-flex flex-col justify-center"> */}
              <div className="relative inline-flex flex-col justify-center">
                <div className="hidden mx-auto border-b border-px border-rock-blue space-x-6">
                  <button
                    type="button"
                    className="appearance-none focus:outline-none py-2 font-medium uppercase
                  tracking-wide text-sm -mb-px border-b border-cornflower text-cornflower"
                  >
                    Via Content Studio
                  </button>
                  <button
                    type="button"
                    className="appearance-none focus:outline-none py-2 font-medium uppercase
                  tracking-wide text-sm -mb-px border-b border-transparent text-light-haiti hover:text-cornflower"
                  >
                    Via API
                  </button>
                  <button
                    type="button"
                    className="appearance-none focus:outline-none py-2 font-medium uppercase
                  tracking-wide text-sm -mb-px border-b border-transparent text-light-haiti hover:text-cornflower"
                  >
                    Via Custom Frontend
                  </button>
                </div>
                <div className="pt-12 md:-mr-84 md:-ml-16">
                  <Image
                    src="/images/illustrations/via-content-studio.svg"
                    className="block md:ml-10 md:-mt-40"
                    alt=""
                    width={1174}
                    height={618}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 md:pt-12">
          <div className="md:flex md:justify-end md:pl-12">
            <div className="w-full md:w-1/2">
              <div className="relative inline-flex items-center pb-4">
                <span className="text-cornflower font-semibold text-sm pr-3">
                  04
                </span>
                <span className="h-0.5 w-24 bg-cornflower" />
              </div>
              <h3 className="font-bold text-xl md:text-2xl lg:text-3xl leading-snug mb-4 text-haiti">
                Build Fast Applications, Faster
              </h3>
              <p className="text-zodiac">
                Power all your applications and websites with unified content.
                Served from a single, secured endpoint.
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-12">
            <div className="w-full md:w-1/2 h-full md:flex md:items-center md:justify-center">
              <Image
                src="/images/for-engineering/build-fast-applications-faster.svg"
                className="md:-mt-24"
                alt=""
                width={699}
                height={628}
              />
            </div>
            <div className="w-full md:w-1/2">
              <div className="space-y-6 py-6">
                {buildFastApplicationsFaster.map((usp, index) => (
                  <USPCardRedesigned
                    key={index}
                    {...usp}
                    theme="light"
                    direction="row"
                  />
                ))}
              </div>
              <Link href="/blog/graphcms-quickstart" prefetch={false}>
                <a className="font-medium group text-cornflower">
                  Get to speed with our quickstart guide
                  <span className="duration-100 group-hover:translate-x-1 inline-block ml-1 transform transition-transform">
                    &#8594;
                  </span>
                </a>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-whisper pb-12 md:pb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6 md:space-y-12">
          <div className="mx-auto space-y-6 md:text-center w-full md:w-3/4">
            <div className="relative inline-flex items-center">
              <span className="text-cornflower font-semibold text-sm pr-3">
                05
              </span>
              <span className="h-0.5 w-24 bg-cornflower" />
            </div>
            <h2 className="text-haiti font-bold text-xl md:text-2xl lg:text-3xl leading-snug">
              Iterate Quickly
            </h2>
            <p className="text-zodiac">
              Don’t be afraid to iterate quickly. GraphCMS gives you all the
              tools for maintaining your development velocity.
            </p>
          </div>

          <div className="flex flex-col-reverse md:flex-col space-y-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 pt-6 md:pb-6">
              {iterateQuickly.map((usp, index) => (
                <USPCardRedesigned key={index} {...usp} theme="light" />
              ))}
            </div>

            <div className="max-w-4xl mx-auto">
              <Image
                src="/images/for-engineering/iterate-quickly.svg"
                className="block"
                alt=""
                width={1015}
                height={452}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white relative">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
          <div className="md:flex md:space-x-12 items-stretch">
            <div className="w-full md:w-2/5 pt-8 pb-4 md:py-32">
              <div className="space-y-6">
                <h2 className="text-haiti font-bold text-3xl lg:text-4xl leading-snug">
                  Integrate with Your Favorite Tools
                </h2>
                <p className="text-zodiac text-lg">
                  GraphCMS has a growing set of integrations to make the best
                  use of the developer ecosystem.
                </p>
                <Button href="https://app.graphcms.com/signup" colour="purple">
                  Sign up for free &#8594;
                </Button>
              </div>
            </div>
            <div className="w-full md:w-3/5 relative py-4">
              <Image
                src="/images/for-engineering/tools.svg"
                alt="Logos of industry tools and software"
                width={692}
                height={572}
              />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export const getStaticProps = async ({ preview = false }) => {
  const { page } =
    (await fetchPage(
      gql`
        query forEngineeringPageQuery {
          page(where: { slug: "for-engineering" }) {
            ...pageData # Fragment is inside the method
          }
        }
      `,
      {
        preview,
      },
    )) || [];

  return {
    props: {
      preview,
      page,
    },
    revalidate: 3600,
  };
};
